import { useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Upload, Trash2, Plus, Loader2, Instagram } from "lucide-react";
import { ICONS } from "@/context/ContentContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const inputCls =
  "w-full rounded-xl border border-transparent bg-cream-100 px-4 py-3 text-sm text-charcoal-900 outline-none transition-colors duration-300 placeholder:text-charcoal-900/30 focus:border-gold/40 focus:bg-cream-50 focus:ring-2 focus:ring-gold/20";

export function Label({ children }) {
  return (
    <span className="mb-2 block font-accent text-[11px] uppercase tracking-[0.2em] text-charcoal-900/50">
      {children}
    </span>
  );
}

export function Txt({ spec, value, onChange }) {
  return (
    <input
      data-testid={`field-${spec.key || "root"}`}
      className={inputCls}
      value={value ?? ""}
      placeholder={spec.label}
      onChange={(e) => onChange(e.target.value)}
    />
  );
}

export function Area({ spec, value, onChange }) {
  return (
    <textarea
      data-testid={`field-${spec.key || "root"}`}
      className={`${inputCls} resize-y`}
      rows={4}
      value={value ?? ""}
      placeholder={spec.label}
      onChange={(e) => onChange(e.target.value)}
    />
  );
}

export function Lines({ spec, value, onChange }) {
  return (
    <textarea
      data-testid={`field-${spec.key || "root"}-lines`}
      className={`${inputCls} resize-y font-mono text-[13px]`}
      rows={Math.max(4, (value || []).length + 1)}
      value={(value || []).join("\n")}
      placeholder="Her satır bir öğe"
      onChange={(e) =>
        onChange(e.target.value.split("\n").filter((s) => s.trim() !== ""))
      }
    />
  );
}

export function Media({ spec, value, onChange, token }) {
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);
  const isVideo = spec.type === "video";

  const doUpload = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await axios.post(`${API}/upload`, fd, {
        headers: { Authorization: `Bearer ${token}` },
      });
      onChange(data.url);
      toast.success("Dosya yüklendi");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Yükleme başarısız");
    } finally {
      setUploading(false);
    }
  };

  return (
    <div data-testid={`field-${spec.key || "root"}-media`} className="space-y-3">
      {value ? (
        <div className="relative w-40">
          {isVideo ? (
            <video src={value} className="h-24 w-40 rounded-2xl object-cover" muted />
          ) : (
            <img src={value} alt="" className="h-24 w-40 rounded-2xl object-cover shadow-premium" />
          )}
        </div>
      ) : null}
      <div className="flex flex-col gap-2.5 sm:flex-row">
        <input
          className={inputCls}
          value={value ?? ""}
          placeholder="https://... veya yükleyin"
          onChange={(e) => onChange(e.target.value)}
        />
        <button
          type="button"
          data-testid={`field-${spec.key || "root"}-upload`}
          onClick={() => fileRef.current?.click()}
          disabled={uploading}
          className="inline-flex shrink-0 items-center justify-center gap-2 rounded-full border border-charcoal/10 px-5 py-3 text-sm font-medium text-charcoal-900 transition-colors duration-300 hover:bg-charcoal/5 disabled:opacity-50"
        >
          {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" strokeWidth={1.5} />}
          {uploading ? "Yükleniyor" : "Yükle"}
        </button>
        <input
          ref={fileRef}
          type="file"
          accept={isVideo ? "video/mp4,video/webm" : "image/jpeg,image/png,image/webp,image/gif"}
          className="hidden"
          onChange={(e) => doUpload(e.target.files?.[0])}
        />
      </div>
    </div>
  );
}

export function IconPick({ spec, value, onChange }) {
  return (
    <div className="grid grid-cols-6 gap-2 sm:grid-cols-9">
      {Object.entries(ICONS).map(([name, Icon]) => (
        <button
          key={name}
          type="button"
          data-testid={`icon-${name}`}
          title={name}
          onClick={() => onChange(name)}
          className={`flex h-10 w-10 items-center justify-center rounded-xl border transition-all duration-300 ${
            value === name
              ? "border-gold bg-gold/15 text-gold-dark shadow-premium"
              : "border-charcoal/10 bg-cream-100 text-charcoal-900/60 hover:border-gold/40 hover:text-gold-dark"
          }`}
        >
          <Icon className="h-4 w-4" strokeWidth={1.5} />
        </button>
      ))}
    </div>
  );
}

export function Sel({ spec, value, onChange }) {
  return (
    <div className="flex flex-wrap gap-2">
      {spec.options.map((o) => (
        <button
          key={o.value}
          type="button"
          data-testid={`select-${o.value}`}
          onClick={() => onChange(o.value)}
          className={`rounded-full border px-4 py-2 text-xs font-medium transition-all duration-300 ${
            value === o.value
              ? "border-gold bg-gold/15 text-gold-dark"
              : "border-charcoal/10 bg-cream-100 text-charcoal-900/60 hover:border-gold/40"
          }`}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

export function ReelField({ spec, value, onChange, token, item, onItem }) {
  const [loading, setLoading] = useState(false);

  const fetchReel = async () => {
    const url = (value || "").trim();
    if (!url) {
      toast.error("Önce Instagram reel bağlantısını yapıştırın");
      return;
    }
    setLoading(true);
    try {
      const { data } = await axios.post(
        `${API}/reel/fetch`,
        { url },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const patch = { reel: data.reel };
      if (data.poster) patch.poster = data.poster;
      if (!item?.id || item.id === "yeni-video") patch.id = `reel-${data.code}`;
      onItem?.(patch);
      toast.success(
        data.poster
          ? "Reel çekildi ve kapak görseli getirildi"
          : "Reel eklendi (kapak bulunamadı, dilerseniz kapak yükleyin)"
      );
    } catch (e) {
      toast.error(e.response?.data?.detail || "Reel çekilemedi, bağlantıyı kontrol edin");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-2.5">
      <div className="flex flex-col gap-2.5 sm:flex-row">
        <input
          data-testid="reel-url-input"
          className={inputCls}
          value={value ?? ""}
          placeholder="https://www.instagram.com/reel/XXXXXXX/"
          onChange={(e) => onChange(e.target.value)}
        />
        <button
          type="button"
          data-testid="reel-fetch-button"
          onClick={fetchReel}
          disabled={loading}
          className="inline-flex shrink-0 items-center justify-center gap-2 rounded-full bg-charcoal-900 px-6 py-3 text-sm font-semibold text-white transition-all duration-300 hover:bg-gold-dark disabled:opacity-50"
        >
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Instagram className="h-4 w-4" strokeWidth={1.5} />}
          {loading ? "Çekiliyor" : "Reel'i Çek"}
        </button>
      </div>
      <div className="flex items-center gap-3">
        {item?.poster ? (
          <img src={item.poster} alt="" className="h-16 w-16 rounded-xl object-cover shadow-premium" />
        ) : null}
        <p className="text-xs text-charcoal-900/50">
          {item?.poster
            ? "Kapak görseli hazır. Kaydedince sitede yayınlanır."
            : "Bağlantıyı yapıştırıp \"Reel'i Çek\" butonuna basın; kapak otomatik gelir."}
        </p>
      </div>
    </div>
  );
}

const FIELD_COMPONENTS = () => ({
  area: Area,
  lines: Lines,
  image: Media,
  video: Media,
  icon: IconPick,
  select: Sel,
  reel: ReelField,
  array: Arr,
  group: GroupField,
  text: Txt,
});

export function Field(props) {
  const C = FIELD_COMPONENTS()[props.spec.type] || Txt;
  return <C {...props} />;
}

export function Arr({ spec, value, onChange, token }) {
  const items = Array.isArray(value) ? value : [];
  const update = (i, v) => onChange(items.map((it, idx) => (idx === i ? v : it)));
  const remove = (i) => onChange(items.filter((_, idx) => idx !== i));
  const add = () => onChange([...items, { ...spec.template }]);

  return (
    <div className="space-y-4">
      {items.map((item, i) => (
        <div
          key={i}
          data-testid={`array-item-${i}`}
          className="rounded-2xl border border-charcoal/5 bg-cream-50 p-5 shadow-premium"
        >
          <div className="mb-4 flex items-center justify-between">
            <span className="font-accent text-[11px] uppercase tracking-[0.2em] text-charcoal-900/45">
              {item[spec.itemTitle] || `Öğe ${i + 1}`}
            </span>
            <button
              type="button"
              data-testid={`array-item-${i}-delete`}
              onClick={() => remove(i)}
              aria-label="Sil"
              className="flex h-8 w-8 items-center justify-center rounded-full border border-rose-300/50 text-rose-600 transition-colors duration-300 hover:bg-rose-100"
            >
              <Trash2 className="h-3.5 w-3.5" strokeWidth={1.5} />
            </button>
          </div>
          <div className="grid gap-4">
            {spec.itemFields.map((f) => (
              <div key={f.key}>
                <Label>{f.label}</Label>
                <Field
                  spec={f}
                  value={item[f.key]}
                  token={token}
                  item={item}
                  onItem={(patch) => update(i, { ...item, ...patch })}
                  onChange={(v) => update(i, { ...item, [f.key]: v })}
                />
              </div>
            ))}
          </div>
        </div>
      ))}
      <button
        type="button"
        data-testid="array-add-button"
        onClick={add}
        className="inline-flex items-center gap-2 rounded-full border border-dashed border-gold/50 px-5 py-3 text-sm font-medium text-gold-dark transition-colors duration-300 hover:bg-gold/10"
      >
        <Plus className="h-4 w-4" strokeWidth={1.5} />
        Yeni Ekle
      </button>
    </div>
  );
}

export function GroupField({ spec, value, onChange, token }) {
  const obj = value || {};
  return (
    <div className="grid gap-4 rounded-2xl border border-charcoal/5 bg-cream-100/50 p-5">
      {spec.fields.map((f) => (
        <div key={f.key}>
          <Label>{f.label}</Label>
          <Field
            spec={f}
            value={obj[f.key]}
            token={token}
            onChange={(v) => onChange({ ...obj, [f.key]: v })}
          />
        </div>
      ))}
    </div>
  );
}
